import debug from "@cher-ami/debug"
import gsap from "gsap"
import { Component } from "~/libs/compose"
import PostSlide from "../postSlide/PostSlide"
import SmoothScrollManager from "~/services/SmoothScrollManager"
import PostHand from "../postHand/postHand"
import postsNavigationService from "~/services/PostsNavigationService"
import { isMobile } from "~/helpers/isMobile"

const componentName = "PostSlider"
const log = debug(`front:${componentName}`)

const DRAG_THRESHOLD = 150 // Number of pixels to drag before triggering a slide
const DRAG_TRESHOLD_MOBILE = 45 // Number of pixels to drag before triggering a slide on mobile
const DRAG_TRESHOLD_LOCK_SCROLL = 8 // Number of pixels to drag before locking the scroll
const DRAG_SCROLL_PREVENT_TRESHOLD = 12 // Number of pixels to scroll before preventing the drag

type TStaticProps = {}

/**
 * @name PostSlider
 */
export default class PostSlider extends Component<TStaticProps> {
	static attrName = componentName

	public gap = 0
	public startOffsetIndex = 0
	public playInOffset = 1
	public loopOffset = 1 // has of the side slides visible on each side

	// drag
	private isDragging: boolean = false
	private startX: number
	private currentX: number

	// TODO: set -1 for anim from the left
	public currentSlideIndex: number

	public currentSlide: PostSlide
	public slideWidth: number
	public translateXOffset: number

	public isAnimating: boolean = false

	public slides: PostSlide[] = this.addAll(PostSlide)

	public hand: PostHand

	public elements = {
		$container: this.find<HTMLDivElement>("container")
	}
	isAnimatingFromDrag: boolean

	// ----------------------------------------------------------------------------- LIFECYCLE

	mounted() {
		log("> mounted")

		this.slideWidth = this.getSlideWidth()
		this.translateXOffset = this.slideWidth + this.gap
		this.startOffsetIndex = +this.$root.dataset.startIndex
		this.currentSlideIndex = this.startOffsetIndex - this.playInOffset
		this.initSlider()
		this.attachEvents()
		this.loadRequiredContent()
	}

	unmounted() {
		this.detachEvents()
	}

	// ----------------------------------------------------------------------------- EVENTS

	attachEvents() {
		// add resize
		window.addEventListener("resize", this.handleResize)
		// Scroll event listener
		window.addEventListener("scroll", this.onScroll)
		// Drag event listeners
		this.elements.$container.addEventListener("mousedown", this.startDrag)
		this.elements.$container.addEventListener("touchstart", this.startDrag, {
			passive: true
		})
	}

	detachEvents() {
		// remove resize
		window.removeEventListener("resize", this.handleResize)
		// Scroll event listener
		window.removeEventListener("scroll", this.onScroll)
		// Drag event listeners
		this.elements.$container.removeEventListener("mousedown", this.startDrag)
		this.elements.$container.removeEventListener("touchstart", this.startDrag)
		document.removeEventListener("mousemove", this.onDrag)
		document.removeEventListener("touchmove", this.onDrag)
		document.removeEventListener("mouseup", this.stopDrag)
		document.removeEventListener("touchend", this.stopDrag)
	}

	// ----------------------------------------------------------------------------- EVENTS ALLBACKS

	handleResize = () => {
		// if slide width has not changed, do nothing
		if (this.slideWidth === this.getSlideWidth()) return
		this.slideWidth = this.getSlideWidth()
		this.translateXOffset = this.slideWidth + this.gap
		this.initSlider()
		log("> handleResize")
	}

	onScroll = () => {
		this.updateSideSlidePositions()
		// drag cursor scroll control
		if (window.scrollY > DRAG_SCROLL_PREVENT_TRESHOLD) {
			this.elements.$container.style.cursor = "initial"
		} else {
			this.elements.$container.style.cursor = "grab"
		}
	}

	// ----------------------------------------------------------------------------- DRAGGING

	startDrag = (event: MouseEvent | TouchEvent) => {
		if (window.scrollY > DRAG_SCROLL_PREVENT_TRESHOLD) return

		this.isDragging = true
		this.startX = this.getEventX(event)
		this.currentX = this.startX

		document.addEventListener("mousemove", this.onDrag)
		document.addEventListener("touchmove", this.onDrag)
		document.addEventListener("mouseup", this.stopDrag)
		document.addEventListener("touchend", this.stopDrag)
	}

	onDrag = (event: MouseEvent | TouchEvent) => {
		if (window.scrollY > DRAG_SCROLL_PREVENT_TRESHOLD) {
			this.stopDrag(event)
			// reset cursor
			this.elements.$container.style.cursor = ""
			return
		}
		if (!this.isDragging) return

		const x = this.getEventX(event)
		const deltaX = x - this.currentX
		this.currentX = x
		const mvtX = this.currentX - this.startX

		this.elements.$container.style.cursor = "grabbing"

		// if started to drag horizontally then stop scroll
		const treshold = DRAG_TRESHOLD_LOCK_SCROLL
		if (Math.abs(mvtX) > treshold && !SmoothScrollManager.lenis.isLocked) {
			SmoothScrollManager.lenis.stop()
		}

		// Animate the container to follow the drag
		gsap.to(this.elements.$container, {
			x: `+=${deltaX}`,
			duration: 0
		})
	}

	stopDrag = (event: MouseEvent | TouchEvent) => {
		this.isDragging = false

		document.removeEventListener("mousemove", this.onDrag)
		document.removeEventListener("touchmove", this.onDrag)
		document.removeEventListener("mouseup", this.stopDrag)
		document.removeEventListener("touchend", this.stopDrag)

		this.elements.$container.style.cursor = "grab"

		// Determine if the threshold has been exceeded
		const totalDragDistance = this.currentX - this.startX
		const treshold = isMobile() ? DRAG_TRESHOLD_MOBILE : DRAG_THRESHOLD
		if (Math.abs(totalDragDistance) > treshold) {
			// Determine direction and animate to the next/previous slide
			const direction = totalDragDistance > 0 ? -1 : 1
			this.isAnimatingFromDrag = true
			postsNavigationService.updateCurrentPost(this.currentSlideIndex + direction)
		}

		// Snap container back
		gsap.to(this.elements.$container, {
			x: 0,
			duration: 0.75,
			ease: "power2.out"
		})

		SmoothScrollManager.lenis.start()
	}

	getEventX = (event: MouseEvent | TouchEvent): number => {
		return event instanceof MouseEvent ? event.pageX : event.touches[0].pageX
	}

	// ----------------------------------------------------------------------------- SLIDE LOGIC

	initSlider() {
		// Animate the slides to the new position
		const wrapWidth = this.getWrapWidth()
		const translateXOffset = this.translateXOffset
		const loopOffset = this.loopOffset
		gsap.set(
			this.slides.map((s) => s.$root),
			{
				x: (x) => (x - this.currentSlideIndex) * this.translateXOffset,
				modifiers: {
					x: (x, target) => {
						// wrap reset position to loop in both directions
						x = gsap.utils.wrap(
							-translateXOffset - translateXOffset * loopOffset,
							wrapWidth - translateXOffset * loopOffset,
							parseInt(x)
						)
						return x + "px"
					}
				}
			}
		)
		log("> init slider," + this.currentSlideIndex)
	}

	/**
	 * Animate to a specific slide by index
	 * @param targetIndex - index of the slide to go to
	 * @param duration - duration of the animation
	 * @returns - the animation tween
	 *
	 *
	 * IMPORTANT : do not use this method directly, use postsNavigationService.updateCurrentPost(index) instead
	 * for updating the other components and services
	 */
	async moveTo(targetIndex: number, duration: number = 0.6): Promise<gsap.core.Tween> {
		if (targetIndex === this.currentSlideIndex) return
		if (this.isAnimating) return
		this.isAnimating = true

		// Normalize the targetIndex to be within the bounds of the slides array
		const totalSlides = this.slides.length
		targetIndex = Math.abs(((targetIndex % totalSlides) + totalSlides) % totalSlides)

		// Determine the shortest direction to the target index
		const currentIndex = this.currentSlideIndex
		const forwardDistance = (targetIndex - currentIndex + totalSlides) % totalSlides
		const backwardDistance = (currentIndex - targetIndex + totalSlides) % totalSlides

		// Use the shortest distance
		const distance =
			forwardDistance <= backwardDistance ? forwardDistance : -backwardDistance

		// Calculate the new position
		const newPosition = `-=${distance * this.translateXOffset}`

		// Animate the slides to the new position
		const currentSlideIndex = this.currentSlideIndex

		// stop the scroll
		SmoothScrollManager.lenis.stop()

		// reset side slide positions
		this.resetSideSlidePositions()

		// reset current slide content width on mobile
		if (isMobile()) {
			this.resetCurrentSlideContentWidth()
		}

		// Preload content for target slide and its neighbors
		const targetSlide = this.slides[targetIndex]
		if (targetSlide) {
			// Load content for this slide if needed
			await targetSlide.loadContent()

			// Preload content for the next slide in the direction we're moving
			const nextIndex = (targetIndex + 1) % totalSlides
			const prevIndex = (targetIndex - 1 + totalSlides) % totalSlides

			// Start loading the next and previous slide content
			Promise.all([
				this.slides[nextIndex].shouldLoadContent(targetIndex)
					? this.slides[nextIndex].loadContent()
					: Promise.resolve(),
				this.slides[prevIndex].shouldLoadContent(targetIndex)
					? this.slides[prevIndex].loadContent()
					: Promise.resolve()
			]).catch((error) => console.error("Error preloading content:", error))
		}

		// set target as current slide
		this.setCurrentSlide(targetIndex)

		this.hand.playOut()
		gsap.delayedCall(0.4, () => {
			this.hand.updateLogo(this.currentSlide.logoSrc)
		})

		// back to top smoothly
		gsap.delayedCall(0.05, () => {
			SmoothScrollManager.lenis.scrollTo(0, {
				force: true,
				duration: 0.5
			})
		})

		// animate the slides to the new position then play the hand animation
		return this.animateSlides(newPosition, duration, currentSlideIndex, () => {
			log("> animateSlides complete")
			this.isAnimating = false
			this.isAnimatingFromDrag = false
			// TODO: move out of the animateSlides method
			this.hand.playIn()
			// Allow the scroll to resume after hand appear
			setTimeout(() => {
				SmoothScrollManager.lenis.start()
			}, 300)
		})
	}

	/**
	 * Animate the slides to the new position
	 * @param newPosition - new position to animate to
	 * @param duration - duration of the animation
	 * @param currentSlideIndex - current slide index
	 * @param onComplete - callback to call when the animation is complete
	 * @returns - the animation tween
	 */
	private animateSlides(
		newPosition: string,
		duration: number,
		currentSlideIndex: number,
		onComplete?: () => void
	):
		| gsap.core.Omit<gsap.core.Tween, "then">
		| PromiseLike<gsap.core.Omit<gsap.core.Tween, "then">> {
		const slideElements = this.getSlidesElements()
		const wrapWidth = this.getWrapWidth()
		const translateXOffset = this.translateXOffset
		const loopOffset = this.loopOffset
		const totalSlides = this.slides.length
		const isAnimatingFromDrag = this.isAnimatingFromDrag
		// Animate the slides to the new position
		return gsap.to(slideElements, {
			x: newPosition,
			duration,
			ease: "power2.out",
			modifiers: {
				x: (x, target) => {
					// wrap reset position to loop in both directions
					x = gsap.utils.wrap(
						-translateXOffset - translateXOffset * loopOffset,
						wrapWidth - translateXOffset * loopOffset,
						parseInt(x)
					)
					return x + "px"
				}
			},
			stagger: function (index, target, list) {
				// Calculate the stagger based on the shortest path to the target
				let forwardIndex = (index - currentSlideIndex + totalSlides) % totalSlides
				let backwardIndex = (currentSlideIndex - index + totalSlides) % totalSlides

				// Choose the shortest path to the target
				let shortestPathIndex =
					forwardIndex <= backwardIndex ? forwardIndex : -backwardIndex
				let direction = forwardIndex <= backwardIndex

				// Get the stagger delay when going left direction (invserse)
				if (!direction) {
					shortestPathIndex = -shortestPathIndex
				}

				// Calculate the stagger delay based on the shortest path index
				const staggerDelay = shortestPathIndex * (isAnimatingFromDrag ? 0.02 : 0.04)
				return staggerDelay
			},
			onComplete
		})
	}

	getWrapWidth() {
		return (this.slides.length - 1) * this.translateXOffset
	}

	getSlideWidth() {
		return this.slides[0].$root.offsetWidth
	}

	getSlidesElements() {
		return this.slides.map((s) => s.$root)
	}

	setCurrentSlide(index: number) {
		// set current slide
		this.currentSlideIndex = index
		// set current slide element
		this.currentSlide = this.slides[index]
		// set active
		this.slides.map((s) => (s.isCurrent = false))
		this.currentSlide.isCurrent = true
		// TODO: refacto pass in isCurrent
		// set active class
		this.slides.map((s) => s.$root.classList.remove("active"))
		this.currentSlide.$root.classList.add("active")

		// set root height to the current slide height
		this.updateContainerHeight(this.currentSlide.$root.offsetHeight)

		// update url with the current slide url
		history.pushState(null, "", this.currentSlide.url)

		// listen to resize event from the current slide to update the container height
		this.currentSlide.emitter.on("article:resize", ({ width, height }) => {
			this.updateContainerHeight(height)
		})
		// remove the listener from the previous slide
		this.slides.map((s) => {
			if (s !== this.currentSlide) {
				s.emitter.off("article:resize")
			}
		})
	}

	updateContainerHeight(value: number) {
		this.elements.$container.style.height = value + "px"
	}

	// ----------------------------------------------------------------------------- SCROLL LOGIC

	getLeftSlideContent() {
		const currentIndex = this.currentSlideIndex
		const totalSlides = this.slides.length
		const leftIndex = (currentIndex - 1 + totalSlides) % totalSlides
		return this.slides[leftIndex].elements.$content
	}

	getRightSlideContent() {
		const currentIndex = this.currentSlideIndex
		const totalSlides = this.slides.length
		const rightIndex = (currentIndex + 1) % totalSlides
		return this.slides[rightIndex].elements.$content
	}

	// Function to update slide positions based on scroll
	updateSideSlidePositions() {
		if (this.isAnimating) return
		const leftSlide = this.getLeftSlideContent()
		const rightSlide = this.getRightSlideContent()

		// Get the scroll position as a percentage of the page height
		const scrollPercent = Math.max(0, window.scrollY / 200)

		// Calculate the translation amount based on the scroll percentage
		const maxTranslation = 33 // Max translation percentage
		const translation = maxTranslation * scrollPercent

		// Calculate rotation for hand from 0 to 1
		const rotation = 1 - scrollPercent

		// Apply the translation to the left and right slides
		;[leftSlide, rightSlide].forEach((slide, index) => {
			// Determine if the slide is on the left or right
			const isLeftSlide = index % 2 === 0 // Example logic for left slide
			const isRightSlide = index % 2 !== 0 // Example logic for right slide

			// Calculate the x position for left and right slides
			let xPosition = 0
			if (isLeftSlide) {
				xPosition = -translation
			} else if (isRightSlide) {
				xPosition = translation
			}

			// Use GSAP to update the position of the slide
			gsap.to(slide, {
				xPercent: xPosition,
				ease: "power2.out",
				duration: 0.2,
				overwrite: "auto"
			})

			// increase the width of the post-article__box on mobile when scrolling
			if (isMobile()) {
				gsap.to(".post-slide.active .post-article__box", {
					width: gsap.utils.clamp(80.2, 93, 80.2 + scrollPercent * 7.8) + "vw",
					duration: 0.2
				})
			}
		})
	}

	resetSideSlidePositions() {
		const leftSlide = this.getLeftSlideContent()
		const rightSlide = this.getRightSlideContent()

		;[leftSlide, rightSlide].forEach((slide) => {
			gsap.to(slide, {
				xPercent: 0,
				ease: "power2.out",
				duration: 0.2,
				overwrite: "auto"
			})
		})
	}

	resetCurrentSlideContentWidth() {
		gsap.to(".post-slide.active .post-article__box", {
			width: 80.2 + "vw",
			duration: 0.2
		})
	}

	// ----------------------------------------------------------------------------- CONTENT LOADING

	/**
	 * Load content for slides that need it based on current position
	 */
	loadRequiredContent() {
		const currentIndex = this.currentSlideIndex
		const totalSlides = this.slides.length

		// Get indexes of slides that should have content (current, prev, next)
		const prevIndex = (currentIndex - 1 + totalSlides) % totalSlides
		const nextIndex = (currentIndex + 1) % totalSlides

		// Array of indexes to load
		const slidesToLoad = [currentIndex, prevIndex, nextIndex]

		// Load content for these slides
		slidesToLoad.forEach((index) => {
			const slide = this.slides[index]
			if (slide && slide.shouldLoadContent(currentIndex)) {
				slide.loadContent()
			}
		})
	}

	// ----------------------------------------------------------------------------- ANIMATION

	override playIn(delay): void {
		gsap.delayedCall(delay, () => {
			// this.goTo(this.currentSlideIndex + this.playInOffset, 0.7)
		})
	}
}
